// Layout imbriqué pour /console/super-admin/*
// Gère l'auth check (super_admin uniquement) + fournit SuperAdminShell.

import { onMount, createSignal, Show, type JSX } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { SuperAdminShell } from "../../operator/console/super-admin-shell";
import { ConfirmProvider } from "../../operator/shared/confirm-dialog";

interface SessionData {
  user: { id?: string; email?: string | null; name?: string | null; role?: string; roles?: string[] };
}

type LegacyMeData = SessionData["user"] & { user?: SessionData["user"] };

export default function ConsoleSuperAdminLayout(props: { children?: JSX.Element }) {
  const navigate = useNavigate();
  const [session, setSession] = createSignal<SessionData | null>(null);
  const [checked, setChecked] = createSignal(false);

  async function loadSession(): Promise<SessionData | null> {
    const betterAuth = await fetch("/api/auth/get-session", { credentials: "include" }).catch(() => null);
    if (betterAuth?.ok) {
      const data = (await betterAuth.json().catch(() => null)) as SessionData | null;
      if (data?.user) return data;
    }

    const legacy = await fetch("/auth/me", { credentials: "include" }).catch(() => null);
    if (!legacy?.ok) return null;
    const data = (await legacy.json().catch(() => null)) as LegacyMeData | null;
    const user = data?.user || (data?.id ? data : null);
    return user ? { user } : null;
  }

  function normalizeRoles(user: SessionData["user"]): string[] {
    if (Array.isArray(user.roles)) return user.roles;
    if (typeof user.role === "string" && user.role) return [user.role];
    return [];
  }

  onMount(async () => {
    try {
      const data = await loadSession();
      if (!data?.user) {
        navigate("/login", { replace: true });
        return;
      }
      data.user.roles = normalizeRoles(data.user);
      if (!data.user.roles.includes("super_admin")) {
        navigate("/console/session", { replace: true });
        return;
      }
      setSession(data);
    } catch (_err) {
      navigate("/login", { replace: true });
    } finally {
      setChecked(true);
    }
  });

  return (
    <Show
      when={checked() && session()}
      fallback={
        <div
          style={{
            display: "flex",
            "align-items": "center",
            "justify-content": "center",
            "min-height": "100vh",
            background: "#0f172a",
            color: "#94a3b8",
            "font-family": "system-ui, sans-serif",
          }}
        >
          Vérification session…
        </div>
      }
    >
      <ConfirmProvider>
        <SuperAdminShell userEmail={session()!.user.email || undefined}>{props.children}</SuperAdminShell>
      </ConfirmProvider>
    </Show>
  );
}
